import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Grid, Card, CardMedia, Skeleton, Button, Paper, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { getFavoriteGames, addFavoriteGame, FavoriteGame, reorderFavoriteGames, removeFavoriteGame } from '../../services/contents_api';
import GameSelectDialog, { GameSelectResult } from '../dialogs/GameSelectDialog';
import AddIcon from '@mui/icons-material/Add';
import { SortableGameGrid } from '../common/SortableGameGrid';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GeneratedShareButton from '../common/GeneratedShareButton';
import { generateFavoritesThumbnail } from '../../utils/generateFavoritesThumbnail';

const MAX_FAVORITES = 16;
const VISIBLE_COUNT = 8;

interface FavoriteGamesWidgetProps {
  username?: string;
  displayName?: string;
  currentUsername?: string;
}

// カバー画像のURLを生成する関数
const getCoverUrl = (coverId: string | null) => {
  if (!coverId) return '/image/no_image.png';
  if (coverId.startsWith('pulse_')) {
    return `https://plsdb.com/cover/${coverId.replace('pulse_', '')}.jpg`;
  }
  return `https://images.igdb.com/igdb/image/upload/t_cover_big/${coverId}.jpg`;
};

const EmptySlot: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      position: 'relative',
      paddingTop: '133%',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      }
    }}
  >
    <IconButton
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        color: 'text.secondary'
      }}
    >
      <AddIcon />
    </IconButton>
  </Card>
);

// 共通のPaperスタイルを定義
const commonPaperStyle = {
  p: 2.5,
  height: '100%',
  borderRadius: 2,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  background: 'background.paper',
  backdropFilter: 'blur(10px)',
  position: 'relative',
};

const FavoriteGamesWidget: React.FC<FavoriteGamesWidgetProps> = ({ username, displayName, currentUsername }) => {
  const [favorites, setFavorites] = useState<FavoriteGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [editingFavorites, setEditingFavorites] = useState<FavoriteGame[]>([]);
  const [expanded, setExpanded] = useState(false);
  const expandRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const fetchFavorites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFavoriteGames(username);
      setFavorites(data);
    } catch (error) {
      console.error('お気に入りゲームの取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites, username]);

  useEffect(() => {
    if (editMode) {
      setEditingFavorites([...favorites]);
    }
  }, [editMode]);

  // expandable部分の高さを計測
  useEffect(() => {
    if (expandRef.current) {
      setContentHeight(expandRef.current.scrollHeight);
    }
  }, [favorites, editMode, editingFavorites]);

  const handleAddGames = async (games: GameSelectResult[]) => {
    try {
      const startOrder = favorites.length;
      const newFavorites: FavoriteGame[] = [];
      for (let i = 0; i < games.length; i++) {
        const newFavorite = await addFavoriteGame(games[i].game_id, startOrder + i);
        newFavorites.push(newFavorite);
      }
      setFavorites(prev => [...prev, ...newFavorites].sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('お気に入りゲームの追加に失敗しました:', error);
    }
  };

  const handleOpenSearch = () => {
    setSearchOpen(true);
  };

  const handleDelete = (gameId: number) => {
    setEditingFavorites(prev => prev.filter(f => f.game_id !== gameId));
  };

  const handleReorder = (newItems: any[]) => {
    setEditingFavorites(prev => newItems.map((item, index) => ({
      ...prev.find(f => f.game_id === item.id)!,
      order: index
    })));
  };

  const handleEditComplete = async () => {
    try {
      const deletedItems = favorites.filter(f =>
        !editingFavorites.some(ef => ef.game_id === f.game_id)
      );

      const reorderedItems = editingFavorites.map((item, index) => ({
        id: item.game_id,
        order: index
      }));

      for (const item of deletedItems) {
        await removeFavoriteGame(item.game_id);
      }

      if (reorderedItems.length > 0) {
        await reorderFavoriteGames(reorderedItems);
      }

      setFavorites(editingFavorites);
      setEditMode(false);
    } catch (error) {
      console.error('お気に入りの更新に失敗しました:', error);
      setEditingFavorites([...favorites]);
    }
  };

  // 表示用のデータを分割
  const visibleFavorites = favorites.slice(0, VISIBLE_COUNT);
  const extraFavorites = favorites.slice(VISIBLE_COUNT);

  // 自分のプロフィールで、8個以上登録済み or 編集モードの場合に展開ボタンを表示
  const showExpandButton = !editMode && (
    extraFavorites.length > 0 ||
    (!username && favorites.length >= VISIBLE_COUNT)
  );

  // 9個目以降に実際のゲームがあるかどうか
  const hasExtraContent = extraFavorites.length > 0;

  if (loading) {
    return (
      <Paper elevation={0} sx={commonPaperStyle}>
        <Typography variant="subtitle2" color="text.secondary">ベストゲーム</Typography>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {[...Array(VISIBLE_COUNT)].map((_, index) => (
            <Grid item xs={3} sm={3} md={1.5} key={index}>
              <Box sx={{ position: 'relative', paddingTop: '133%' }}>
                <Skeleton
                  variant="rectangular"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: 1
                  }}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{
      ...commonPaperStyle,
      p: { xs: 2, sm: 2.5 },
      ...(showExpandButton && !hasExtraContent && !expanded && {
        pb: { xs: 0.5, sm: 1 },
      }),
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" lineHeight={1}>
            {username ? `@${username}のベストゲーム` : 'ベストゲーム'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {favorites.length > 0 && !editMode && !username && currentUsername && (
            <GeneratedShareButton
              generateImage={() => generateFavoritesThumbnail(favorites, displayName || currentUsername || '')}
              shareUrl={`${process.env.REACT_APP_FRONTEND_URL}/share/favorites/${currentUsername}/`}
              shareText={`${displayName || currentUsername}のベストゲーム`}
              downloadFileName={`${displayName || currentUsername}-best-games`}
              hashtags={['PULSE_DB', 'BestGames']}
              menuTitle="ベストゲームを共有"
            />
          )}
          {!username && favorites.length > 0 && (
            <>
              {editMode ? (
                <>
                  <Button
                    startIcon={<CloseIcon />}
                    size="small"
                    variant="outlined"
                    onClick={() => setEditMode(false)}
                    color="error"
                  >
                    キャンセル
                  </Button>
                  <Button
                    startIcon={<DoneIcon />}
                    size="small"
                    variant="outlined"
                    onClick={handleEditComplete}
                    color="success"
                  >
                    完了
                  </Button>
                </>
              ) : (
                <Button
                  startIcon={<EditIcon />}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setEditMode(true);
                    setExpanded(true);
                  }}
                  color="primary"
                  sx={{ height: '28px' }}
                >
                  編集
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>

      {editMode && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            border: '1px solid rgba(25, 118, 210, 0.2)',
            borderRadius: 1
          }}
        >
          <Typography variant="body2" color="primary">
            タイトルの削除と、ドラッグ＆ドロップで並び替えができます
          </Typography>
        </Box>
      )}

      {editMode ? (
        <SortableGameGrid
          items={editingFavorites.map(f => ({
            id: f.game_id,
            name: f.game_japanese_name || f.game_name,
            cover_url: getCoverUrl(f.game_cover),
            order: f.order
          }))}
          onReorder={handleReorder}
          onDelete={handleDelete}
          maxItems={MAX_FAVORITES}
          gridTemplateColumns={{
            xs: 'repeat(4, 1fr)',
            sm: 'repeat(8, 1fr)'
          }}
        />
      ) : (
        <>
          {favorites.length === 0 && !username ? (
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={3} sm={3} md={1.5}>
                <EmptySlot onClick={handleOpenSearch} />
              </Grid>
            </Grid>
          ) : favorites.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                borderRadius: 1
              }}
            >
              <Typography variant="body2" color="text.secondary">
                まだお気に入りゲームが登録されていません
              </Typography>
            </Box>
          ) : (
            <>
              {/* 最初の8個 (常に表示) */}
              <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                {visibleFavorites.map((favorite) => (
                  <Grid item xs={3} sm={3} md={1.5} key={favorite.id}>
                    <Link
                      to={`/game/${favorite.game_id}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <Card
                        sx={{
                          position: 'relative',
                          paddingTop: '133%',
                          backgroundColor: 'rgba(255, 255, 255, 0.02)',
                          borderRadius: 1
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={getCoverUrl(favorite.game_cover)}
                          alt={favorite.game_japanese_name || favorite.game_name}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 1
                          }}
                        />
                      </Card>
                    </Link>
                  </Grid>
                ))}

                {/* 最初の8個の空きスロット */}
                {!username && favorites.length < VISIBLE_COUNT && [...Array(VISIBLE_COUNT - favorites.length)].map((_, index) => (
                  <Grid item xs={3} sm={3} md={1.5} key={`empty-${index}`}>
                    <EmptySlot onClick={handleOpenSearch} />
                  </Grid>
                ))}
              </Grid>

              {/* 9-16個目 (フェード開閉) */}
              {showExpandButton && (
                <Box sx={{ position: 'relative', mt: { xs: 1, sm: 1.5 } }}>
                  <Box
                    ref={expandRef}
                    sx={{
                      maxHeight: expanded
                        ? `${contentHeight + 30}px`
                        : hasExtraContent
                          ? { xs: '60px', sm: '70px' }
                          : '0px',
                      overflow: 'hidden',
                      transition: expanded
                        ? 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                        : 'max-height 0.4s cubic-bezier(0.4, 0, 0.6, 1)',
                    }}
                  >
                    <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                      {extraFavorites.map((favorite) => (
                        <Grid item xs={3} sm={3} md={1.5} key={favorite.id}>
                          <Link
                            to={`/game/${favorite.game_id}`}
                            style={{ textDecoration: 'none', pointerEvents: expanded ? 'auto' : 'none' }}
                          >
                            <Card
                              sx={{
                                position: 'relative',
                                paddingTop: '133%',
                                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                                borderRadius: 1
                              }}
                            >
                              <CardMedia
                                component="img"
                                image={getCoverUrl(favorite.game_cover)}
                                alt={favorite.game_japanese_name || favorite.game_name}
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: 1
                                }}
                              />
                            </Card>
                          </Link>
                        </Grid>
                      ))}

                      {/* 9-16個目の空きスロット */}
                      {!username && favorites.length >= VISIBLE_COUNT && [...Array(MAX_FAVORITES - Math.max(favorites.length, VISIBLE_COUNT))].map((_, index) => (
                        <Grid item xs={3} sm={3} md={1.5} key={`empty-extra-${index}`}>
                          <EmptySlot onClick={handleOpenSearch} />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>

                  {/* グラデーションオーバーレイ + クリックエリア */}
                  <Box
                    onClick={() => setExpanded(!expanded)}
                    sx={{
                      position: hasExtraContent ? 'absolute' : 'relative',
                      bottom: hasExtraContent ? 0 : 'auto',
                      left: 0,
                      right: 0,
                      height: expanded
                        ? 0
                        : hasExtraContent
                          ? { xs: '90px', sm: '70px' }
                          : '32px',
                      background: hasExtraContent
                        ? (theme) =>
                            `linear-gradient(to bottom, transparent 0%, ${theme.palette.background.paper}e6 70%, ${theme.palette.background.paper} 100%)`
                        : 'none',
                      cursor: 'pointer',
                      transition: expanded
                        ? 'opacity 0.2s ease-out, height 0s 0.2s'
                        : 'opacity 0.3s 0.15s ease-in, height 0s 0s',
                      opacity: expanded ? 0 : 1,
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      pb: 0.5,
                    }}
                  >
                    {!hasExtraContent && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          opacity: 0.5,
                          fontSize: '0.7rem',
                          mr: 0.5,
                        }}
                      >
                        拡張可能
                      </Typography>
                    )}
                    <KeyboardArrowDownIcon
                      sx={{
                        fontSize: '1.2rem',
                        color: 'text.secondary',
                        opacity: expanded ? 0 : 0.7,
                      }}
                    />
                  </Box>

                  {/* 閉じるボタン (展開時のみ) */}
                  {expanded && (
                    <Box
                      onClick={() => setExpanded(false)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 1,
                        py: 0.5,
                        cursor: 'pointer',
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        }
                      }}
                    >
                      <KeyboardArrowDownIcon
                        sx={{
                          fontSize: '1.2rem',
                          color: 'text.secondary',
                          transform: 'rotate(180deg)',
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </>
      )}

      <GameSelectDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onMultiSelect={handleAddGames}
        multiSelect
        maxSelect={MAX_FAVORITES - favorites.length}
        title="お気に入りゲームを追加"
        excludeGameIds={favorites.map(f => f.game_id)}
      />
    </Paper>
  );
};

export default FavoriteGamesWidget;
